/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import InfiniteScrollBase from "react-infinite-scroll-component";
import { makeStyles, TableCell, TableRow as TableRowMui } from "@material-ui/core";
import { Observer } from "mobx-react";
import { MarkHighlighter, Spinner } from "@common";
import { EmptyList } from "@search/SearchResult/EmptyList";
import locale from "@bonliva-crm/locale";
import { stringNormalize } from "@bonliva-crm/common";
import cns from "classnames";
import { AutoSizer } from "react-virtualized";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import { TableSearchColumn } from "@search/TableSearch";

export type Props = {
  scrollContainer: HTMLElement;
  items: any[];
  loadMore: () => void;
  dataLength: number; // current length of items (not total)
  hasMore: boolean;
  renderRow: (item, index) => React.ReactNode;
  search?: string;
  renderHead?: React.ReactNode;
  loading?: boolean;
  offsetTopHead?: string;
};

export type RowProps = {
  item: any;
  onItemOpen?: (id: string) => void;
  rowIndex: number;
  intrinsicSize?: string;
  columns: Array<TableSearchColumn<any>>;
  rowClassNames?: string;
};

export const DefaultInfinityTableRow = React.memo(
  ({ item, onItemOpen, rowIndex, intrinsicSize, columns, rowClassNames }: RowProps) => {
    const classes = useRowStyles({ intrinsicSize });
    const rowClassName = cns(classes.flexContainer, classes.bodyRow, rowClassNames, {
      [classes.rowOdd]: rowIndex % 2 !== 1
    });
    return (
      <AutoSizer disableHeight>
        {({ width }) => (
          <ColumnSizer columns={columns} width={width}>
            {({ ref }) => (
              <TableRowMui
                component="div"
                key={rowIndex}
                style={{
                  width: ref.getTotalWidth()
                }}
                className={rowClassName}
                onClick={() => onItemOpen && onItemOpen(item._id)}
              >
                {columns
                  .filter((x) => !x.skip)
                  .map((column, columnIndex) => {
                    const width = ref.getColumnWidth(column.dataKey);
                    const className = cns("table-search-cell", classes.flexContainer, {
                      [classes.cellVAlignCenter]: column.vAlign === "center",
                      [classes.padded]: !column.fitted,
                      [classes.cell]: true
                    });

                    return (
                      <TableCell
                        key={column.dataKey}
                        component="div"
                        className={className}
                        style={{ ...column.style, ...column, width }}
                      >
                        {column.cellRenderer({
                          rowData: item,
                          ...column,
                          isScrolling: false,
                          columnIndex,
                          rowIndex
                        })}
                      </TableCell>
                    );
                  })}
              </TableRowMui>
            )}
          </ColumnSizer>
        )}
      </AutoSizer>
    );
  }
);

export const InfiniteScroll = React.memo(
  ({
    scrollContainer,
    hasMore,
    loadMore,
    dataLength,
    renderHead,
    renderRow,
    items,
    search,
    loading,
    offsetTopHead
  }: Props) => {
    const classes = useStyles({ offsetTopHead });
    return (
      <Observer>
        {() => {
          return (
            <div className={classes.wrapper}>
              <MarkHighlighter highlightSearch={[search, stringNormalize(search)]}>
                {renderHead && <div className={classes.theadWrap}>{renderHead}</div>}
                <InfiniteScrollBase
                  scrollableTarget={scrollContainer as any}
                  // 'dataLength' Should be the diff between own previous value.
                  // It doesn't matter if it diff between the total count of our list
                  dataLength={dataLength}
                  style={{ overflow: "initial" }}
                  next={loadMore}
                  hasMore={hasMore}
                  scrollThreshold={0.99}
                  loader={
                    <div style={{ padding: 10 }} key={"spinner"}>
                      <Spinner />
                    </div>
                  }
                  endMessage={<EmptyList>{locale.NoMoreSearchResults}</EmptyList>}
                >
                  {items.map((item, index) => (
                    <React.Fragment key={item._id ?? index}>
                      {renderRow(item, index)}
                    </React.Fragment>
                  ))}
                </InfiniteScrollBase>
                {hasMore && loading && <div style={{ width: "100%", height: 100 }} />}
              </MarkHighlighter>
            </div>
          );
        }}
      </Observer>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
    margin: "10px 0"
  },
  theadWrap: {
    display: "flex",
    // justifyContent: "space-between",
    position: "sticky",
    top: ({ offsetTopHead }: any) => offsetTopHead ?? 0,
    zIndex: 1
  },
  row: {
    // width: "100%",
    lineHeight: 1,
    wordBreak: "break-word",
    padding: "4px"
  }
}));

const useRowStyles = makeStyles((theme) => ({
  flexContainer: {
    display: "flex",
    boxSizing: "border-box"
  },
  bodyRow: {
    borderRight: `1px solid ${theme.palette.grey["200"]}`,
    borderLeft: `1px solid ${theme.palette.grey["200"]}`,
    outline: "none",
    cursor: "pointer",
    contentVisibility: ({ intrinsicSize }: any) =>
      `${intrinsicSize ? "auto" : "initial"}`,
    containIntrinsicSize: ({ intrinsicSize }: any) => `${intrinsicSize ?? "initial"}`,
    transition: theme.transitions.create("background-color"),
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.grey[200]
    }
  },
  rowOdd: {
    backgroundColor: "#e3e3e340"
  },
  cellVAlignCenter: {
    alignItems: "center"
  },
  padded: {
    padding: "0 8px"
  },
  cell: {
    borderBottom: "none"
  }
}));
